import { FxpHttpService } from "@fxp/fxpservices";
import { forwardRef, Inject, Injectable } from "@angular/core";
import "rxjs/Rx"; // todo: need to import submodule instead
import "rxjs/add/operator/map"; // todo: need to import submodule instead

import { APIConstants, CacheKeys, CacheStorageOptions, OneProfileGraphQLConstants, OneProfileGraphQLFields, OrgType, Services, SkillsHierarchyCategoryId, SkillStatus } from "../application.constants";
import { CacheService } from "./cache.service";
import { ConfigManagerService } from "./configmanager.service";
import { DataService } from "./data.service";
import { ICityDetails, ICountryDetails, IMasterSkillsLevels, IOneProfileBulkAPIResponse, IOneProfileTrainingAPIResponse, IRoleSkills, IStateDetails, IOneProfileAPIResponse, IRoleDomainSkill, ILocation, ISkillDetails, ISubconOnboardingSnapshot } from "./contracts/one-profile.contracts";
import { IOneProfileSerResAttr } from "../../components/tiles/type-ahead/type-ahead-contracts";
import { SharedFunctionsService } from "./sharedfunctions.service";
import { Observable } from "rxjs/Rx";
import { from } from "rxjs";
import { DmServiceAbstract } from "../abstraction/dm-service.abstract";
import { DMLoggerService } from "./dmlogger.service";
import { ICustomerEngagementDomain } from "./contracts/add-roles.contracts";
import { IBulkResourceProfilesData, ICity, IMasterEntityData, IMasterGeoLocation, IMasterGeoLocationData, IMasterSkillData, IResourceBasicProfile, IResourceBasicProfileData, ISearchProfile, ISearchProfileData, ISearchSkillsData, IServiceDeliveryOrganization, ISkillDetailsHierarchy, ISkillSearchResult, IState, ISubconOnboardingSnapshotData, OneProfileGraphQLResponse } from "./contracts/one-profile-graphql.contracts";
import { IOneProfileCompanyCodeAttr, IOneProfileCompanyCodeAttrV2 } from "../../components/tiles/type-ahead-companycode/type-ahead-companycode-contracts";
import { switchMap } from "rxjs/operators";
import { OneProfileGraphQLService } from "./one-profile-graphql-service";
import { ProjectService } from "./project.service";
@Injectable()
export class OneProfileService extends DmServiceAbstract {

    private roleSkills: IRoleDomainSkill[] = [];
    private oneProfileBaseUri: string;
    private oneProfileCoreBaseUri: string;
    private oneProfileGraphQLEndpoint: string;
    private searchCompanyCodeCanceler: Promise<{}>;
    private takeCitiesLimit: string;
    private showOPLevel2Skills: boolean;
    private isGraphQLAPIDefaultEnabled: boolean = false;
    private isGraphQLEnabled: boolean;
    private defaultPageSize: number = 1000;

    public constructor(
        @Inject(forwardRef(() => FxpHttpService)) private fxpHttpService: FxpHttpService,
        @Inject(ConfigManagerService) private configManagerService: ConfigManagerService,
        @Inject(DataService) private dataService: DataService,
        @Inject(SharedFunctionsService) private sharedFunctionsService: SharedFunctionsService,
        @Inject(ProjectService) private projectService: ProjectService,
        @Inject(CacheService) private cacheService: CacheService,
        @Inject(DMLoggerService) dmLogger: DMLoggerService,
        @Inject(OneProfileGraphQLService) private oneProfileGQService: OneProfileGraphQLService,
    ) {
        super(dmLogger, Services.OneProfileService);

        this.configManagerService.initialize().then(() => {
            this.oneProfileBaseUri = this.configManagerService.getValue<string>("oneProfileBaseUri");
            this.oneProfileCoreBaseUri = this.configManagerService.getValue<string>("oneProfileCoreBaseUri");
            this.oneProfileGraphQLEndpoint = this.configManagerService.getValue<string>("oneProfileGraphQLEndpoint");
            this.takeCitiesLimit = this.configManagerService.getValue<string>("takeCitiesLimit");
            this.defaultPageSize = this.configManagerService.getValue<number>("oneProfileGraphQLPageSize") || this.defaultPageSize;
            this.isGraphQLAPIDefaultEnabled = this.configManagerService.getValue<boolean>("isGraphQLAPIDefaultEnabled");
            this.showOPLevel2Skills = this.configManagerService.isFeatureEnabled("showOPLevel2Skills");
        });
        this.isGraphQLEnabled = null;
    }

    /**
     * Gets a profile from One Profile based on the given alias.
     *
     * @param {string} alias
     * @returns {Promise<IOneProfileBulkAPIResponse[]>}
     * @memberof OneProfileService
     */
    public async getProfile(alias: string): Promise<IOneProfileAPIResponse> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        if (enableGraphQLAPI) {
            return this.oneProfileGQService.fetchUserProfileGraphQL(alias).then((response: OneProfileGraphQLResponse<IResourceBasicProfileData>) => {
                if (response && response.data && response.data.resourceBasicProfile) {
                    return this.oneProfileGQService.mapResourceBasicProfileToProfile(response.data.resourceBasicProfile);
                } else if (response && response.errors && response.errors.length > 0) {
                    return Promise.reject(response);
                }
                return null;
            });
        }
        else {
            const url = this.oneProfileBaseUri + `/api/v2.0/profile/${alias}`;
            return this.dataService.getData(url, "", APIConstants.BulkUserBasicDetails);
        }
    }

    /**
     * Gets a list of profiles from One Profile based on the given list of aliases.
     *
     * @param {string[]} aliasArray
     * @returns {Promise<IOneProfileBulkAPIResponse[]>}
     * @memberof OneProfileService
     */
    public async getBulkProfiles(aliasArray: string[]): Promise<IOneProfileBulkAPIResponse[]> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        if (enableGraphQLAPI) {
            return this.oneProfileGQService.fetchBulkUserProfileGraphQL(aliasArray).then((response: OneProfileGraphQLResponse<IBulkResourceProfilesData>) => {
                const userProfiles: IOneProfileBulkAPIResponse[] = [];
                if (response && response.data && response.data.bulkResourceProfiles) {
                    if (Array.isArray(response.data.bulkResourceProfiles)) {
                        response.data.bulkResourceProfiles.forEach((resourceBasicProfile: IResourceBasicProfile) => {
                            userProfiles.push(this.oneProfileGQService.mapBulkResourceBasicProfile(resourceBasicProfile));
                        });
                    }
                } else if (response && response.errors && response.errors.length > 0) {
                    return Promise.reject(response);
                }
                return userProfiles;
            });
        }
        else {
            const url = this.oneProfileBaseUri + "/api/v2.0/profile/getbulkprofile";
            return this.dataService.postData(url, "", APIConstants.BulkUserBasicDetails, {
                Alias: aliasArray
            });
        }
    }

    /**
     * A getter for the Role Skills list to be accessed in other components such as Type Ahead Skills.
     * Just returns the role skills we have already retrieved from the API--does not call the API.
     *
     * @returns {IRoleDomainSkill[]}
     * @memberof OneProfileService
     */
    public getRoleSkills(): IRoleDomainSkill[] {
        return this.roleSkills;
    }

    /**
     * Search skills based on api, from the api
     *
     * @returns {IRoleDomainSkill[]}
     * @memberof OneProfileService
     */
    public async getSkillsFromApiBasedOnSearchText(searchText: string, orgType: string): Promise<ISkillDetails[]> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        if (enableGraphQLAPI) {
            return this.oneProfileGQService.fetchSkillsGraphQLBySearchText(searchText, orgType).then((response: OneProfileGraphQLResponse<ISearchSkillsData>) => {
                const skills: ISkillDetails[] = [];
                if (response && response.data && response.data.searchSkills && response.data.searchSkills.searchResults) {
                    if (Array.isArray(response.data.searchSkills.searchResults)) {
                        response.data.searchSkills.searchResults.forEach((skill: ISkillSearchResult) => {
                            skills.push(this.oneProfileGQService.mapSearchSkills(skill));
                        });
                    }
                }
                else if (response && response.errors && response.errors.length > 0) {
                    return Promise.reject(response);
                }
                return skills;
            });
        }
        else {
            const url = this.oneProfileBaseUri + `/api/v2.0/master/skills?take=20&gracePeriod=1&statusfilter=New,Retiring,Active&searchText=${searchText}&orgtype=${orgType}`;
            return this.dataService.getData(url, "", APIConstants.BulkUserBasicDetails);
        }
    }


    /**
     * Search user skills with a given grace period from One Profile API.
     * Gets Product Skills, Technical skills and Functional skills based on the category ID (requires separate API calls.)
     * @param gracePeriod
     */
    public searchUserSkills(gracePeriod: number): Promise<IRoleSkills[]> {
        let skillsList: IMasterSkillsLevels[] = [];
        const productSkillsPromise = this.cacheService.get(CacheKeys.ProductSkills.KeyName,
            () => this.GetSkillsListByCategory(SkillsHierarchyCategoryId.Product, gracePeriod), CacheKeys.ProductSkills.Duration, CacheStorageOptions.LocalStorage)
            .then((response) => {
                if (response) {
                    skillsList = skillsList.concat(response);
                }
            });
        const functionalSkillsPromise = this.cacheService.get(CacheKeys.FunctionalSkills.KeyName,
            () => this.GetSkillsListByCategory(SkillsHierarchyCategoryId.Functional, gracePeriod), CacheKeys.FunctionalSkills.Duration, CacheStorageOptions.LocalStorage)
            .then((response) => {
                if (response) {
                    skillsList = skillsList.concat(response);
                }
            });
        const technicalSkillsPromise = this.cacheService.get(CacheKeys.TechnicalSkills.KeyName,
            () => this.GetSkillsListByCategory(SkillsHierarchyCategoryId.Technical, gracePeriod), CacheKeys.TechnicalSkills.Duration, CacheStorageOptions.LocalStorage)
            .then((response) => {
                if (response) {
                    skillsList = skillsList.concat(response);
                }
            });

        return Promise.all([productSkillsPromise, functionalSkillsPromise, technicalSkillsPromise])
            .then(() => {
                this.roleSkills = []; // resets the role skills list to get a fresh call of the data.
                skillsList.forEach((level1: IMasterSkillsLevels) => {
                    if (level1 && level1.MasterSkillLevels && level1.MasterSkillLevels.length) {
                        level1.MasterSkillLevels.forEach((level2: IMasterSkillsLevels) => {
                            if (this.showOPLevel2Skills) {
                                const roleSkill: IRoleDomainSkill = {
                                    JobSkillId: level2.JobSkillId,
                                    JobSkillValue: level2.LevelValue,
                                    Domains: level2.DimensionStr && level2.DimensionStr.length > 0 ? this.sharedFunctionsService.getDomainDetailsFromDimensions(level2.DimensionStr) : this.sharedFunctionsService.getDomainFromSkill(level2)
                                };
                                if (level2.SkillStatus === SkillStatus.Retiring) {
                                    roleSkill.JobSkillValue = roleSkill.JobSkillValue + " (" + level2.SkillStatus + ")";
                                }
                                this.roleSkills.push(roleSkill);
                            } else {
                                if (level2 && level2.MasterSkillLevels && level2.MasterSkillLevels.length) {
                                    level2.MasterSkillLevels.forEach((level3: IMasterSkillsLevels) => {
                                        const roleSkill: IRoleDomainSkill = {
                                            JobSkillId: level3.JobSkillId,
                                            JobSkillValue: level3.LevelValue,
                                            Domains: level3.DimensionStr && level3.DimensionStr.length > 0 ? this.sharedFunctionsService.getDomainDetailsFromDimensions(level3.DimensionStr) : this.sharedFunctionsService.getDomainFromSkill(level3)
                                        };
                                        if (level3.SkillStatus === SkillStatus.Retiring) {
                                            roleSkill.JobSkillValue = roleSkill.JobSkillValue + " (" + level3.SkillStatus + ")";
                                        }
                                        this.roleSkills.push(roleSkill);
                                    });
                                }
                            }
                        });
                    }
                });
                return Promise.resolve(this.roleSkills);
            });
    }

    /**
     * Gets a list of skills based on the given category ID from the One Profile API
     * @param categoryId
     */
    public async GetSkillsListByCategory(categoryId: string, gracePeriod: number): Promise<IMasterSkillsLevels[]> {
        const orgType = this.showOPLevel2Skills ? OrgType.MCS : OrgType.Consulting;
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        if (enableGraphQLAPI) {
            const graphQLFields: string[] = OneProfileGraphQLFields.SkillsHierarchy;
            // Recursive function to fetch paginated data
            return this.oneProfileGQService.fetchAllPages<IMasterSkillData, IMasterSkillsLevels>(
                (pageNumber) => this.oneProfileGQService.getSkillsHierarchyGraphQuery(categoryId, gracePeriod, orgType, graphQLFields, pageNumber),
                APIConstants.FetchSkillsListGraphQLByCategory,
                async (response, skillList) => {
                    if (response && response.data && response.data.masterSkill && response.data.masterSkill.skillHierarchy && response.data.masterSkill.skillHierarchy.length > 0) {
                        response.data.masterSkill.skillHierarchy.forEach((skillDetailsHierarchy: ISkillDetailsHierarchy) => {
                            skillList.push(this.oneProfileGQService.mapSkillDetailsHierarchy(skillDetailsHierarchy));
                        });
                        return Promise.resolve(response.data.masterSkill.skillHierarchy.length === this.defaultPageSize); // Continue fetching
                    }
                    else if (response && response.errors && response.errors.length > 0) {
                        return Promise.reject(response);
                    }
                    return Promise.resolve(false); // Stop fetching
                }
            );
        }
        else {
            const url = this.oneProfileBaseUri + `/api/v2.0/master/skillshierarchy?categoryid=${categoryId}&OrgType=${orgType}&Statusfilter=New,Active,Retiring&Graceperiod=` + gracePeriod.toString();
            const headers = {
                "Content-Type": "application/json",
                "retryEnabled": false
            };
            return this.fxpHttpService.get(url, headers).then((response) => {
                if (response && response.data) {
                    return Promise.resolve(response.data);
                }
            });
        }
    }

    /**
     * Gets the list if countries from the One Profile API
     */
    public async getCountries(): Promise<ICountryDetails[]> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        if (enableGraphQLAPI) {
            const graphQLFields: string[] = OneProfileGraphQLFields.GetCountries;
            // Recursive function to fetch paginated data
            return this.oneProfileGQService.fetchAllPages<IMasterGeoLocationData, ICountryDetails>(
                (pageNumber) => this.oneProfileGQService.getGeoLocationGraphQuery(graphQLFields, pageNumber),
                APIConstants.FetchCountriesGraphQL,
                async (response, countries) => {
                    if (response && response.data && response.data.masterGeoLocation && response.data.masterGeoLocation.length > 0) {
                        response.data.masterGeoLocation.forEach((country: IMasterGeoLocation) => {
                            countries.push({ CountryCode: country.countryCode, CountryName: country.countryName } as ICountryDetails);
                        });
                        return Promise.resolve(response.data.masterGeoLocation.length === this.defaultPageSize); // Continue fetching
                    }
                    else if (response && response.errors && response.errors.length > 0) {
                        return Promise.reject(response);
                    }
                    return Promise.resolve(false); // Stop fetching
                }
            );
        } else {
            const url = this.oneProfileBaseUri + "/api/v1/master/country";
            return this.dataService.getData(url, "", APIConstants.GetCountries);
        }
    }

    /**
     * Gets a list of states based on the country code from the One Profile API
     * @param countryCode
     */
    public async getStatesBasedOnCountryCode(countryCode: string): Promise<IStateDetails[]> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        if (enableGraphQLAPI) {
            const isStatelessCountry: boolean = await this.oneProfileGQService.isStatelessCountry(countryCode);
            if (isStatelessCountry) {
                const mockState: IStateDetails[] = [{
                    StateName: " "
                }];
                return Promise.resolve(mockState);
            }
            const graphQLFields: string[] = OneProfileGraphQLFields.GetStatesBasedOnCountryCode;
            return this.oneProfileGQService.fetchAllPages<IMasterGeoLocationData, IStateDetails>(
                (pageNumber) => this.oneProfileGQService.getGeoLocationGraphQuery(graphQLFields, pageNumber, this.defaultPageSize, { countryCode: true }, countryCode),
                APIConstants.FetchStatesGraphQL,
                async (response, states) => {
                    if (response && response.data && response.data.masterGeoLocation && response.data.masterGeoLocation.length > 0
                        && response.data.masterGeoLocation[0].states && response.data.masterGeoLocation[0].states.length > 0) {
                        response.data.masterGeoLocation[0].states.forEach((state: IState) => {
                            states.push({ StateCode: state.stateCode, StateName: state.stateName } as IStateDetails);
                        });
                        return Promise.resolve(response.data.masterGeoLocation[0].states.length === this.defaultPageSize); // Continue fetching
                    }
                    else if (response && response.errors && response.errors.length > 0) {
                        return Promise.reject(response);
                    }
                    return Promise.resolve(false); // Stop fetching
                }
            );
        }
        else {
            // todo change API url back to v2.0 for this function
            const url = this.oneProfileBaseUri + "/api/v1/master/" + countryCode + "/states";
            return this.dataService.getData(url, "", APIConstants.GetStatesBasedOnCountry);
        }
    }

    /**
     * Gets a list of cities based on the given country code and state name from the One Profile API
     * @param countryCode
     * @param stateName
     */
    public async getCitiesBasedOnCountryCodeStateName(countryCode: string, stateName: string): Promise<ICityDetails[]> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        if (enableGraphQLAPI) {
            const graphQLFields: string[] = OneProfileGraphQLFields.GetCitiesBasedOnStateCountry;
            return this.oneProfileGQService.fetchAllPages<IMasterGeoLocationData, ICityDetails>(
                (pageNumber) => countryCode ? this.oneProfileGQService.getGeoLocationGraphQuery(graphQLFields, pageNumber, this.defaultPageSize, { countryCode: true, stateName: true }, countryCode, stateName) : "",
                APIConstants.FetchCityBasedOnCountryCodeGraphQL,
                async (response, cities) => {
                    if (response && response.data && response.data.masterGeoLocation && response.data.masterGeoLocation.length > 0
                        && response.data.masterGeoLocation[0].states && response.data.masterGeoLocation[0].states.length > 0) {
                        let currentPageSize: number = 0;
                        response.data.masterGeoLocation[0].states.forEach((state) => {
                            if (state && state.cities && state.cities.length > 0) {
                                state.cities.forEach((city: ICity) => {
                                    cities.push({ CityName: city.cityName } as ICityDetails);
                                    currentPageSize++;
                                });
                            }
                        });
                        return Promise.resolve(currentPageSize === this.defaultPageSize); // Continue fetching
                    }
                    else if (response && response.errors && response.errors.length > 0) {
                        // Handle GraphQL errors if needed
                        return Promise.reject(response);
                    }
                    return Promise.resolve(false); // Stop fetching
                }
            );

        } else {
            const url = `${this.oneProfileBaseUri}/api/v1/master/citylist?country=${countryCode}&state=${stateName}`;
            return this.dataService.getData(url, "", APIConstants.GetCitiesBasedOnStateCountry);
        }
    }

    /**
     * Gets a list of cities based on the country, state or city search text from the One Profile API
     * @param searchText
     */
    public async getCitiesBasedOnSearchText(searchText: string): Promise<ILocation[]> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();

        const url = enableGraphQLAPI ? this.oneProfileGraphQLEndpoint : `${this.oneProfileBaseUri}/api/v2.0/master/cities?searchtext=${searchText}&take=${this.takeCitiesLimit}`;
        if (enableGraphQLAPI) {
            return this.oneProfileGQService.fetchCitiesBasedOnSearchTextGraphQL(searchText).then((response: OneProfileGraphQLResponse<IMasterGeoLocationData>) => {
                const cities: ILocation[] = [];
                if (response && response.data && response.data.masterGeoLocation && response.data.masterGeoLocation.length > 0) {
                    if (Array.isArray(response.data.masterGeoLocation)) {
                        response.data.masterGeoLocation.forEach((location: IMasterGeoLocation) => {
                            location.states.forEach((state: IState) => {
                                state.cities.forEach((city: ICity) => {
                                    cities.push({
                                        City: city.cityName,
                                        State: state.stateName,
                                        Country: location.countryName,
                                        CountryAlpha2Code: location.countryCode
                                    } as ILocation);
                                });
                            });
                        });
                    }
                } else if (response && response.errors && response.errors.length > 0) {
                    return Promise.reject(response);
                }
                return cities;
            });
        } else {
            return this.dataService.getData(url, "", APIConstants.GetCitiesBasedOnStateCountry);
        }
    }

    /**
     * Gets a list of resource training details based on the given resource alias
     * @param resourceAlias
     */
    public getResourceTrainingDetails(resourceAlias: string): Promise<IOneProfileTrainingAPIResponse[]> {
        const url = this.isGraphQLAPIDefaultEnabled ? this.oneProfileGraphQLEndpoint : `${this.oneProfileBaseUri}/api/v2.0/profile/${resourceAlias}/training`;
        if (this.isGraphQLAPIDefaultEnabled) {
            // since this API response is not used anywhere, we are not implementing the GraphQL version of it.
            return Promise.resolve([]);

        }
        else {
            return this.dataService.getData(url, "", APIConstants.GetTrainingDetails);
        }
    }

    /**
     * Calls the One Profile API and searches for profiles that matchthe given search values.
     * Will return FTEs only if the FTE only flag is  true.
     * Will filter out any ignored BPIDs.
     * @param searchValue
     */
    public async profileSearch(searchValue: string, fteOnly: boolean, ignoreBpid: number[], fteAndContingentStaff: boolean = false): Promise<IOneProfileSerResAttr[]> {

        let url = this.oneProfileBaseUri + "/api/v2.0";
        let resourceCategoryTypes = [];

        url += "/profile/searchprofile?searchText=" + searchValue;
        if (fteAndContingentStaff) {
            url += "&resourceCategoryTypes=0,1";
            resourceCategoryTypes = [0, 1];
        } else if (fteOnly) { // For the V2 API
            url += "&resourceCategoryTypes=0";
            resourceCategoryTypes = [0];
        }
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        if (enableGraphQLAPI) {
            return this.oneProfileGQService.fetchProfileSearchGraphQL(searchValue, resourceCategoryTypes).then((response: OneProfileGraphQLResponse<ISearchProfileData>) => {
                let userList: IOneProfileSerResAttr[] = [];
                if (response && response.data && response.data.searchProfiles) {
                    if (Array.isArray(response.data.searchProfiles)) {
                        response.data.searchProfiles.forEach((profile: ISearchProfile) => {
                            userList.push(this.oneProfileGQService.mapSearchProfile(profile));
                        });
                    }
                    userList = userList.filter((x) => x.BusinessPartnerId && ignoreBpid.indexOf(Number(x.BusinessPartnerId)) < 0);
                    userList = userList.slice(0, 8);
                } else if (response && response.errors && response.errors.length > 0) {
                    return Promise.reject(response);
                }
                return userList;
            });
        } else {
            return this.dataService.getData(url, "", "").then((response: IOneProfileSerResAttr[]) => {
                let userList: IOneProfileSerResAttr[] = [];
                if (response) {
                    userList = response.filter((x) => x.BusinessPartnerId && ignoreBpid.indexOf(Number(x.BusinessPartnerId)) < 0);
                    userList = userList.slice(0, 8);
                }
                return userList;

            });
        }
    }

    /**
     * Search Company Codes List with Observable
     * @param searchValue
     */
    public searchCompanyCodesList(searchValue: string): Observable<any> {

        if (this.searchCompanyCodeCanceler) {
            Promise.resolve(this.searchCompanyCodeCanceler);
            this.searchCompanyCodeCanceler = undefined;
        }

        return from(this.isOPGraphQLAPIEnabled()).pipe(
            switchMap((enableGraphQLAPI: boolean) => {
                if (enableGraphQLAPI) {
                    const graphQLFields: string[] = OneProfileGraphQLFields.SearchCompanyCodes;
                    return from(this.oneProfileGQService.fetchAllPages<IMasterEntityData, any>(
                        (pageNumber) => this.oneProfileGQService.getSearchCompanyCodesGraphQuery(graphQLFields, searchValue, pageNumber),
                        APIConstants.FetchCompanyCodesGraphQL,
                        async (response, companyList) => {
                            if (response && response.data && response.data.masterEntity && response.data.masterEntity.companyCodes && response.data.masterEntity.companyCodes.length > 0) {
                                // Check if the response contains data and push it to the states array
                                response.data.masterEntity.companyCodes.forEach((companyCode: IOneProfileCompanyCodeAttrV2) => {
                                    companyList.push({
                                        CompanyCode: companyCode.companyCode,
                                        CompanyName: companyCode.companyName,
                                        CountryCode: companyCode.countryCode,
                                        CurrencyCode: companyCode.currencyCode
                                    } as IOneProfileCompanyCodeAttr);
                                }
                                );
                                return Promise.resolve(response.data.masterEntity.companyCodes.length === this.defaultPageSize); // Continue fetching
                            }
                            else if (response && response.errors && response.errors.length > 0) {
                                // Handle GraphQL errors if needed
                                return Promise.reject(response);
                            }
                            return Promise.resolve(false); // Stop fetching
                        }
                    ));
                } else {
                    const url = `${this.oneProfileBaseUri}/api/v2.0/master/companycode?searchtext=${searchValue}`;
                    return from(this.dataService.getData(url, "", APIConstants.SearchCompanyCodes));
                }
            })
        );
    }

    /**
     * Gets a list of customer engagement domains
     */
    public async getCustomerEngagementDomains(): Promise<ICustomerEngagementDomain[]> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();

        const url = enableGraphQLAPI ? this.oneProfileGraphQLEndpoint : !this.showOPLevel2Skills ? `${this.oneProfileBaseUri}/api/v2.0/master/servicesdeliveryorganization` : `${this.oneProfileBaseUri}/api/v2.0/master/servicesdeliveryorganization?orgType=MCS`;
        if (enableGraphQLAPI) {
            const graphQLFields: string[] = OneProfileGraphQLFields.ServicesDeliveryOrganizations;
            return this.oneProfileGQService.fetchAllPages<IMasterEntityData, ICustomerEngagementDomain>(
                (pageNumber) => !this.showOPLevel2Skills ? "" : this.oneProfileGQService.getCustomerEngagementDomainsGraphQuery(graphQLFields, pageNumber, OrgType.MCS),
                APIConstants.FetchCustomerEngagementDomainsGraphQL,
                async (response, customerEngagementDomains) => {
                    if (response && response.data && response.data.masterEntity && response.data.masterEntity.servicesDeliveryOrganizations && response.data.masterEntity.servicesDeliveryOrganizations.length > 0) {
                        response.data.masterEntity.servicesDeliveryOrganizations.forEach((customerEngagementDomain: IServiceDeliveryOrganization) => {
                            customerEngagementDomains.push({
                                OrgType: customerEngagementDomain.orgType,
                                SDODomainId: customerEngagementDomain.sdoDomainId,
                                ServiceDeliveryOrganizationName: customerEngagementDomain.serviceDeliveryOrganizationName,
                                IsActive: true
                            } as ICustomerEngagementDomain);
                        });
                        return Promise.resolve(response.data.masterEntity.servicesDeliveryOrganizations.length === this.defaultPageSize); // Continue fetching
                    }
                    else if (response && response.errors && response.errors.length > 0) {
                        return Promise.reject(response);
                    }
                    return Promise.resolve(false); // Stop fetching
                }
            );
        }
        else {
            return this.dataService.getData(url, "", APIConstants.GetCustomerEngagementDomains);
        }
    }

    /**
     * Gets a profile from One Profile based on the given bpId.
     *
     * @param {string} bpId
     * @returns {Promise<IOneProfileAPIResponse>}
     * @memberof OneProfileService
     */
    public getProfileBasedOnBpid(bpId: string): Promise<IOneProfileAPIResponse> {
        const url = this.oneProfileBaseUri + `/api/v2.0/profile/${bpId}/basic`;
        return this.dataService.getData(url, "", APIConstants.GetUserDetailsFromBpId);
    }

    public async getSubconOnboardingStatus(userAlias: string): Promise<ISubconOnboardingSnapshot[]> {
        const enableGraphQLAPI: boolean = await this.isOPGraphQLAPIEnabled();
        const url = enableGraphQLAPI ? this.oneProfileGraphQLEndpoint : this.oneProfileCoreBaseUri + `/api/subcononboarding/pjmsnapshot/${userAlias}`;
        if (enableGraphQLAPI) {
            return this.oneProfileGQService.fetchSubconOnboardingGraphQL(userAlias).then((response: OneProfileGraphQLResponse<ISubconOnboardingSnapshotData>) => {
                let subconOnboardingStatus: ISubconOnboardingSnapshot[] = [];
                if (response && response.data && response.data.subconOnboardingSnapshotForPjm) {
                    subconOnboardingStatus = response.data.subconOnboardingSnapshotForPjm;
                } else if (response && response.errors && response.errors.length > 0) {
                    // Handle GraphQL errors if needed
                    return Promise.reject(response);
                }
                return subconOnboardingStatus;
            });

        } else {
            return this.dataService.getData(url, "", APIConstants.GetSubconOnboardingStatusFromAlias, [403]);
        }
    }

    private async isOPGraphQLAPIEnabled(): Promise<boolean> {
        if (this.isGraphQLEnabled !== null) {
            return this.isGraphQLEnabled;
        }
        this.isGraphQLEnabled = await this.cacheService.get(
            CacheKeys.EnableOneProfileGraphQLAPI.KeyName,
            () => this.projectService.getFeatureFlag(OneProfileGraphQLConstants.EnableGraphQLFeatureFlagKey, this.isGraphQLAPIDefaultEnabled),
            CacheKeys.EnableOneProfileGraphQLAPI.Duration
        );
        return this.isGraphQLEnabled;
    }
}
